// The weekly challenge's reads and writes (scenario Q9,
// PLAN-2026-11-01-the-weekly-challenge §5).
//
// ONE WEEK FOR EVERYBODY ON THE SITE, read by its sport: a league's pages
// ask for their league's sport's week, and its board for their league's
// members. A real league's alone: the practice league's simulator has no
// challenge, and a visitor to the demo league has no account to enter with.

import {
  ChallengeBoardView,
  ChallengeNameView,
  ChallengeWeekView,
  type ChallengeBoardViewShape,
  type ChallengeWeekViewShape,
  type Snapshot,
} from '@fsp/domain';
import { messageOf, send } from './api';
import { useRead } from './read';

const query = (season?: number, period?: number, league?: string | null) => {
  const q = new URLSearchParams();
  if (season !== undefined) q.set('season', String(season));
  if (period !== undefined) q.set('period', String(period));
  if (league) q.set('league', league);
  const s = q.toString();
  return s === '' ? '' : `?${s}`;
};

/** A week, the current one when none is asked; nothing is asked for a null sport. */
export function useChallengeWeek(
  sport: string | null,
  season?: number,
  period?: number,
  refresh = 0,
): { snapshot: Snapshot<ChallengeWeekViewShape | null>; reload: () => void } {
  const read = useRead(
    sport === null ? null : `/sports/${sport}/challenge${query(season, period)}`,
    ChallengeWeekView,
    { refresh },
  );
  return { snapshot: read.snapshot, reload: read.reload };
}

/** A week's board: a league's members with `league`, else everybody's top. */
export function useChallengeBoard(
  sport: string | null,
  league: string | null,
  season?: number,
  period?: number,
  refresh = 0,
): Snapshot<ChallengeBoardViewShape | null> {
  return useRead(
    sport === null ? null : `/sports/${sport}/challenge/board${query(season, period, league)}`,
    ChallengeBoardView,
    { refresh },
  ).snapshot;
}

/** My whole entry for a week; the answer is the week as it now stands. */
export async function saveChallengeEntry(
  sport: string,
  season: number,
  period: number,
  picks: (string | null)[],
  captain: string | null,
): Promise<{ ok: true; view: ChallengeWeekViewShape } | { ok: false; message: string }> {
  const reply = await send(
    'PUT',
    `/sports/${sport}/challenge/${season}/${period}`,
    { picks, captain },
    ChallengeWeekView,
  );
  if (reply.status === 'ok') return { ok: true, view: reply.data };
  return {
    ok: false,
    message: messageOf(
      reply,
      'That pick did not save. Try again.',
      'Could not reach the site. Nothing was saved.',
    ),
  };
}

/** The name the public board shows for me. */
export async function saveChallengeName(
  name: string,
): Promise<{ ok: true; name: string } | { ok: false; message: string }> {
  const reply = await send('PUT', '/me/challenge-name', { name }, ChallengeNameView);
  if (reply.status === 'ok') return { ok: true, name: reply.data.name };
  return {
    ok: false,
    message: messageOf(
      reply,
      'That name did not save. Try again.',
      'Could not reach the site. Nothing was saved.',
    ),
  };
}
